import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useModal } from 'mui-modal-provider';
import { isEqual } from 'lodash';

import { IStepperImperativeHandleProps, IStepComponentProps } from '../../../../../../common/stepper';
import { IFormImperativeHandleProps } from '../../../../../../common/form';
import {
  IExternalState,
  IFormItemValues,
  IStepsState,
} from '../../order-create-page.models';
import { AddLensesStepForm, IPackageParams } from './add-lenses-step-form';
import { getParamsByPackage } from './utils/get-params-by-package';
import { AddLensesStepTitle } from './add-lenses-step-title/add-lenses-step-title';
import { AddButton } from './add-button';
import { Button } from '../../../../../../common';
import { useStyles } from './add-lenses-step.styles';
import { getIndexedFormValues } from '../../utils/get-form-values';
import { getConcatFormValues } from '../confirm-step/utils/get-concat-form-values';
import { IFormSubmitValues } from '../../../../../../common/form';
import { getConsumerLenses } from './utils/get-consumer-lenses';
import { getFormPackages } from './utils/get-form-packages';
import { LeaveModal } from '../../leave-modal';
import { LeaveSameRouteModal } from '../../leave-same-route-modal';
import {
  getChangedConsumerLenses,
  getRepeatConsumerLenses,
} from './utils/get-repeat-consumer-lenses';
import { compareFormValues } from './utils/compare-form-values';

export const AddLensesStep = forwardRef<IStepperImperativeHandleProps, IStepComponentProps>(({
  stepsState,
  onGoNextSuccess,
  externalState,
  onGoLastSuccess,
}, ref): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const { products: addLensesStepState } = stepsState as IStepsState;
  const consumerLenses = useMemo(() => {
    if (!(externalState as IExternalState).lenses || !(externalState as IExternalState).brandsData?.brands) return [];

    const hiddenBrands = new Set<string>(
      (externalState as IExternalState).brandsData.brands
        .filter((brand) => brand.hiddenForC2C)
        .map((brand) => brand.product),
    );

    return getParamsByPackage((externalState as IExternalState).lenses!).filter(
      (lens) => lens.dependent
        ? lens.brandId && !hiddenBrands.has(lens.brandId) && lens.dependent?.active
        : lens.brandId && !hiddenBrands.has(lens.brandId),
    );
  }, [(externalState as IExternalState).lenses, (externalState as IExternalState).brandsData?.brands]);

  const classes = useStyles();
  const [packagesValue, setPackagesValue] = useState(0);
  const [consumerProducts, setConsumerProducts] = useState<IPackageParams[]>(consumerLenses);
  const [isScrollElem, setIsScrollElem] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    goNext() {
      formRef.current!.submit();
    },
    goBack() {},
    goLast() {},
  }));

  const renderProducts = useMemo(() => {
    if (addLensesStepState) {
      return (externalState as IExternalState).isRepeatOrder
        ? getRepeatConsumerLenses(consumerProducts, addLensesStepState?.products as IFormSubmitValues)
        : getChangedConsumerLenses(consumerProducts, addLensesStepState?.products as IFormSubmitValues);
    } else {
      return getChangedConsumerLenses(
        consumerProducts.length ? consumerProducts : consumerLenses,
        formRef.current?.getValues(),
      );
    }
  }, [addLensesStepState?.products, consumerProducts, consumerLenses]);

  const values: IFormItemValues[] = useMemo(() => {
    return getIndexedFormValues(renderProducts);
  }, [renderProducts, getIndexedFormValues]);

  const defaultValues = useMemo(() => {
    return getConcatFormValues(values);
  }, [values]);

  const handleSubmit = () => {
    (externalState as IExternalState).consumerProducts = consumerProducts;
    onGoNextSuccess({ products: formRef.current!.getValues() });
  };

  const handleIncreasePackageValue = () => {
    setPackagesValue(packagesValue + 1);
  };

  const handleDecreasePackageValue = () => {
    setPackagesValue(packagesValue - 1);
  };

  const handleGoLastSuccess = () => {
    onGoLastSuccess((externalState as IExternalState).products);
  };

  const { showModal } = useModal();

  const handleGoLastStep = useCallback(() => {
    const isSameValues = (stepsState as IStepsState).isChange && !(externalState as IExternalState).isRepeatOrder
      ? isEqual(formRef.current!.getValues(), addLensesStepState?.products as IFormSubmitValues)
      : compareFormValues(
        formRef.current!.getValues(), addLensesStepState?.products as IFormSubmitValues,
      );
    (stepsState as IStepsState).isChange = false;

    if (!isSameValues) {
      showModal(LeaveSameRouteModal, {
        onConfirm: handleGoLastSuccess,
      });
    } else {
      handleGoLastSuccess();
    }
  }, [onGoLastSuccess]);

  const handleSubmitOnGoLast = useCallback(() => {
    (externalState as IExternalState).products = { products: formRef.current!.getValues() };
    (stepsState as IStepsState).isChange = false;
    (externalState as IExternalState).consumerProducts = consumerProducts;
    onGoLastSuccess({ products: formRef.current!.getValues() });
  }, [
    formRef.current?.getValues(),
    consumerProducts,
    onGoLastSuccess,
  ]);

  const handleAddPackage = (productId: string) => {
    const product = consumerProducts.find((
      consumerProduct,
    ) => consumerProduct.id === productId);

    const idPartial = new Date().getTime();
    const newConsumerProducts = [...consumerProducts, {
      ...product!,
      id: `${productId}-${idPartial}`,
    }];
    setConsumerProducts(newConsumerProducts);
    setIsScrollElem(true);
  };

  useEffect(() => {
    if ((externalState as IExternalState).isChangeARL) {
      const scrollARL = document.querySelector('[data-name="ARL"]');

      if (scrollARL) {
        scrollARL.scrollIntoView();
      }
    }

    return () => {
      (externalState as IExternalState).isChangeARL = false;
    };
  }, [(externalState as IExternalState).isChangeARL]);

  useEffect(() => {
    if (isScrollElem) {
      document.getElementById('consumer-products')!.lastElementChild!.scrollIntoView();
    }

    return () => {
      setIsScrollElem(false);
    };
  }, [isScrollElem]);

  useEffect(() => {
    if ((externalState as IExternalState).consumerProducts?.length) {
      setConsumerProducts((externalState as IExternalState).consumerProducts || consumerLenses);
    }
  }, [(externalState as IExternalState).consumerProducts]);

  useEffect(() => {
    if (addLensesStepState) {
      const orderLenses = getConsumerLenses(addLensesStepState.products as IFormSubmitValues, consumerProducts)!;
      const packages = getFormPackages(orderLenses);

      setPackagesValue(packages);
    } else {
      setPackagesValue((externalState as IExternalState).productQuantity || 0);
    }
  }, []);

  useEffect(() => {
    return () => {
      (externalState as IExternalState).productQuantity = packagesValue;
    };
  }, [packagesValue]);

  return (
    <>
      {(!!packagesValue || (externalState as IExternalState).isRepeatOrder) ? <LeaveModal /> : null}
      <div
        className={packagesValue ? classes.titleBar : undefined}
        data-testid="title-bar"
      >
        <AddLensesStepTitle
          packagesValue={packagesValue}
          onGoLast={handleGoLastStep}
          isRepeatOrder={(externalState as IExternalState).isRepeatOrder}
          isChangeProducts={(stepsState as IStepsState).isChange}
        />
        {packagesValue > 0 && (
          <AddButton
            products={consumerLenses}
            onCardClick={handleAddPackage}
          />
        )}
      </div>
      <AddLensesStepForm
        ref={formRef}
        consumerProducts={renderProducts}
        formProps={{
          validationMode: 'onChange',
          onSubmit: handleSubmit,
          defaultValues,
        }}
        brands={(externalState as IExternalState).brandsData?.brands}
        onIncreasePackageValue={handleIncreasePackageValue}
        onDecreasePackageValue={handleDecreasePackageValue}
        stepState={addLensesStepState?.products as IFormSubmitValues}
      />
      <Button
        variant="contained"
        data-testid="choose-ecp-button"
        fullWidth
        className={classes.button}
        disabled={(packagesValue as number) < 1}
        onClick={((externalState as IExternalState).isRepeatOrder
          || (stepsState as IStepsState).isChange || (externalState as IExternalState).store) ? handleSubmitOnGoLast : handleSubmit}
      >{(externalState as IExternalState).store ?
        <FormattedMessage id="vp.survey.next" /> :
        ((externalState as IExternalState).isRepeatOrder || (stepsState as IStepsState).isChange) ? (
          <FormattedMessage id="order.create.addLenses.repeat.button.label" />
        ) : (
          <FormattedMessage id="order.create.addLenses.button.label" />
        )}
      </Button>
    </>
  );
});
